// client/src/components/CommentSection.js
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import CommentsPage from './CommentsPage';
import api from '../services/api';
import io from 'socket.io-client';

/**
 * CommentSection
 * Affiché sous chaque message d'une édition publique.
 *
 * Props :
 *   messageId      — identifiant du message concerné
 *   currentUserId  — identifiant de l'utilisateur connecté
 *   isCreator      — true si l'utilisateur est le créateur de l'édition
 *   allowComments  — true si l'édition autorise les commentaires
 */
function CommentSection({ messageId, currentUserId, isCreator, isMember, isMemberSelected, allowComments, socket }) {
  const { t } = useTranslation();
  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

  const [commentText, setCommentText]   = useState('');
  const [commentCount, setCommentCount] = useState(0);
  const [showPage, setShowPage]         = useState(false);
  const [submitting, setSubmitting]     = useState(false);
  const [banned, setBanned]             = useState(false);   // retour API 403

  /* ── Charger le nombre de commentaires ── */
  useEffect(() => {
    if (!messageId) return;
    const token = localStorage.getItem('token');
    api.get(`${API_URL}/api/comments/${messageId}/count`, {
      headers: { Authorization: `Bearer ${token}` }
    })
      .then(res => setCommentCount(res.data.count || 0))
      .catch(() => {});
  }, [messageId, API_URL]);

useEffect(() => {
  if (!messageId || !socket) return;

  socket.emit('joinMessageRoom', messageId);

  const handleNewComment = (comment) => {
    // Vérifier que ce commentaire concerne bien CE message
    if (comment.messageId !== messageId) return;
    if (comment.author?._id !== currentUserId) {
      setCommentCount(c => c + 1);
    }
  };

  const handleCommentDeleted = ({ commentId, messageId: deletedMessageId, deletedBy }) => {
    if (deletedMessageId !== messageId) return;
if (deletedBy === currentUserId) return;
    setCommentCount(c => Math.max(0, c - 1));

  };

  socket.on('newComment', handleNewComment);
  socket.on('commentDeleted', handleCommentDeleted);

  return () => {
    socket.off('newComment', handleNewComment);       // ← retire CE listener précis
    socket.off('commentDeleted', handleCommentDeleted);
    socket.emit('leaveMessageRoom', messageId);
  };
}, [messageId, socket, currentUserId]);
  /* ── Poster un commentaire ── */
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!commentText.trim()) return;
    setSubmitting(true);
    const token = localStorage.getItem('token');
    try {
      await api.post(
        `${API_URL}/api/comments/${messageId}`,
        { text: commentText },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setCommentText('');
      setCommentCount(c => c + 1);
    } catch (err) {
      if (err?.response?.status === 403) setBanned(true);
    } finally {
      setSubmitting(false);
    }
  };

  if (!allowComments) return null;
//if (!isMember) return null;

  return (
    <div className="comment-section" style={styles.wrapper}>

      {/* ── Compteur + lien ── */}
      <button
        className="comment-count-btn"
        onClick={() => setShowPage(true)}
        style={styles.countBtn}
      >
        💬 {commentCount > 0
          ? t('diffusion.seeCount', { count: commentCount })
          : t('diffusion.noComments', 'Aucun commentaire')}
      </button>

     {/* ── Champ de saisie ── */}
{(isMember || isMemberSelected) && (
  banned ? (
    <p style={styles.banned}>
      {t(
        'diffusion.banned',
        'Vous êtes banni des commentaires de cette édition.'
      )}
    </p>
  ) : (

    <form onSubmit={handleSubmit} style={styles.form}>
      <input
        type="text"
        value={commentText}
        onChange={e => setCommentText(e.target.value)}
        placeholder={t(
          'diffusion.comments_placeholder',
          'Écrire un commentaire…'
        )}
        style={styles.input}
        maxLength={500}
      />

      <button
        type="submit"
        disabled={submitting || !commentText.trim()}
        style={styles.sendBtn}
        title={t('diffusion.comments_send', 'Envoyer')}
      >
        {submitting ? '…' : '➤'}
      </button>
    </form>

  )
)}
      {/* ── Page complète des commentaires (modale) ── */}
      {showPage && (
        <CommentsPage
          messageId={messageId}
          currentUserId={currentUserId}
          isCreator={isCreator}
          onClose={() => setShowPage(false)}
          onCountChange={delta => setCommentCount(c => c + delta)}
        />
      )}
    </div>
  );
}

/* ── Styles inline légers (remplaçables par du CSS) ── */
const styles = {
  wrapper: {
    borderTop: '1px solid var(--color-border-tertiary)',
    marginTop: '6px',
    paddingTop: '6px',
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
  },
  countBtn: {
    background: 'none',
    border: 'none',
    padding: '0',
    cursor: 'pointer',
    fontSize: '0.82rem',
    color: '#007bff',
    textAlign: 'left',
  },
  form: {
    display: 'flex',
    gap: '6px',
    alignItems: 'center',
  },
  input: {
    flex: 1,
    padding: '4px 8px',
    borderRadius: '16px',
    border: '1px solid var(--color-border-secondary)',
    fontSize: '0.85rem',
    background: 'var(--color-background-secondary)',
    color: 'var(--color-text-primary)',
    outline: 'none',
  },
  sendBtn: {
    background: '#007bff',
    border: 'none',
    borderRadius: '50%',
    width: '28px',
    height: '28px',
    color: '#fff',
    cursor: 'pointer',
    fontSize: '13px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  banned: {
    fontSize: '0.8rem',
    color: 'red',
    fontStyle: 'italic',
    margin: 0,
  },
};

export default CommentSection;
